import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import FormDialog from '../../../components/ui/FormDialog'
import FormField from '../../../components/ui/FormField'
import TextInput from '../../../components/ui/TextInput'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { requiredId, requiredText } from '../../../lib/forms/rules'
import ActionPeopleEditor from '../../actions/components/ActionPeopleEditor'
import RecordGroupField from '../../recordGroups/components/RecordGroupField'
import type { PersonInput } from '../../actions/types'
import type { ManagerList, ManagerListPerson, ManagerListUpsert } from '../types'
// `ActionPeopleEditor` kendi görünümünü `act-*` sınıflarıyla çizer ve o sınıflar actions.css'tedir;
// bu ekran aksiyon rotalarından bağımsız açılabildiği için stil dosyası BURADA içe aktarılır (Vite
// CSS'i bir kez yükler). Aksi halde çip listesi yalnız bu sayfaya doğrudan girildiğinde stilsiz kalırdı.
import '../../actions/actions.css'

/**
 * Liste adı sınırı: **250 karakter** (backend `ManagerListUpsertDto` ile birebir). Kolon
 * `VARCHAR2(1000 BYTE)`'tır; 250 karakterin en kötü hâli (Türkçe harf başına 2 bayt) 500 bayt eder,
 * yani bayt sınırı **fiilen tetiklenemez** — bir ağ olarak durur. İkisi de sunucudaki kuralın aynısı
 * olmak zorunda: ayrışırsa kullanıcı burada kabul edilen bir adı kaydedemez.
 */
const NAME_MAX = 250
const NAME_MAX_BYTES = 1000

const schema = z.object({
  groupId: requiredId(),
  name: requiredText(NAME_MAX, NAME_MAX_BYTES),
})

type FormValues = z.infer<typeof schema>

interface Props {
  visible: boolean
  /** Düzenlenen liste; null → yeni oluşturma. */
  initial: ManagerList | null
  submitting: boolean
  /** Sunucudan gelen mükerrer ad hatası — ad alanının altında gösterilir. */
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: ManagerListUpsert) => void
  onHide: () => void
  /**
   * Salt okunur görüntüleme (126, 26.10.2026 — Kısıtlı Yetkili asistan): kaydetme yok, kişiler
   * eklenip çıkarılamaz. Listenin kişilerinin TAMAMI yalnız bu pop-up'ta görünür (tablo ilk üçünü
   * gösterir), bu yüzden kip ekranı gizlemek yerine pop-up'ı görüntülemeye açar.
   *
   * <p>Grup alanı bu kipte DÜZ METİNDİR: {@link RecordGroupField} seçeneklerini yazma yetkisi isteyen
   * bir uçtan çeker ve salt-okunur türev o ucu açmaz.
   */
  readOnly?: boolean
}

/**
 * Liste ekle/düzenle pop-up'ı (114): üstte **grup** (122) ve **liste adı**, altında **kişi ekleme/çıkarma**.
 *
 * <p>Tek pop-up olması kullanıcı kararıdır (30.09.2026): ad ve kişiler aynı "Kaydet" ile, sunucuda tek
 * işlemde yazılır — ayrı yüzeyler olsaydı "ad kaydedildi ama kişiler kaydedilmedi" gibi yarım bir durum
 * görünür hâle gelirdi.
 *
 * <p>Kişi seçici paylaşılan {@link ActionPeopleEditor}'dür (anayasa → Reuse Mandate): HR araması, sicil
 * bazında tekilleştirme ve çıkarma düğmesi zaten orada yazılı. Kişiler RHF **dışında** yerel durumda
 * tutulur (emsal: `PrincipleDecisionFormDialog`).
 *
 * <p><b>Kişisiz liste geçerlidir</b> — önce ad açılıp kişiler sonra eklenebilsin diye; bu yüzden burada
 * "en az bir kişi" doğrulaması yoktur.
 */
export default function ManagerListFormDialog({
  visible,
  initial,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
  readOnly = false,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError, setValue } = useZodForm(schema, {
    defaultValues: { groupId: null, name: '' },
  })
  const [people, setPeople] = useState<ManagerListPerson[]>([])

  useEffect(() => {
    if (visible) {
      reset({ groupId: initial?.groupId ?? null, name: initial?.name ?? '' })
      setPeople(initial?.people ?? [])
    }
  }, [visible, initial, reset])

  const preselectGroup = useCallback((id: number) => setValue('groupId', id), [setValue])

  useEffect(() => {
    if (nameError) {
      setError('name', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  // Sicil bazında tekil: aynı kişi ikinci kez seçilirse yok sayılır (sunucu da aynı kuralı uygular).
  const addPerson = (p: PersonInput) =>
    setPeople((list) =>
      list.some((x) => x.employeeId === p.employeeId)
        ? list
        : [
            ...list,
            {
              employeeId: p.employeeId,
              fullName: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.employeeId,
              firstName: p.firstName ?? null,
              lastName: p.lastName ?? null,
              businessMail: p.businessMail ?? null,
              jobTitleTr: p.jobTitleTr ?? null,
              jobTitleEn: p.jobTitleEn ?? null,
            },
          ],
    )

  const removePerson = (employeeId: string) =>
    setPeople((list) => list.filter((x) => x.employeeId !== employeeId))

  const onValid = (v: FormValues) =>
    onSubmit({
      groupId: v.groupId as number,
      name: v.name,
      people: people.map((p) => ({
        employeeId: p.employeeId,
        firstName: p.firstName,
        lastName: p.lastName,
        businessMail: p.businessMail,
        jobTitleTr: p.jobTitleTr,
        jobTitleEn: p.jobTitleEn,
      })),
    })

  return (
    <FormDialog
      visible={visible}
      header={
        readOnly
          ? t('managerLists.form.viewTitle')
          : initial
            ? t('managerLists.form.editTitle')
            : t('managerLists.form.newTitle')
      }
      submitLabel={t('managerLists.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
      readOnly={readOnly}
    >
      <div className="admin-form">
        {readOnly ? (
          <FormField label={t('recordGroups.field')} htmlFor="ml-group">
            <TextInput id="ml-group" value={initial?.groupName ?? ''} disabled />
          </FormField>
        ) : (
          <RecordGroupField
            control={control}
            name="groupId"
            visible={visible}
            current={initial ? { id: initial.groupId, name: initial.groupName } : null}
            onPreselect={preselectGroup}
          />
        )}
        <FormTextField
          control={control}
          name="name"
          label={t('managerLists.form.name')}
          required
          maxLength={NAME_MAX}
        />

        <div className="ml-people-field">
          <span className="ml-people-label">
            {t('managerLists.form.people')}
            <span className="ml-people-count">{people.length}</span>
          </span>
          <ActionPeopleEditor
            people={people}
            editable={!readOnly}
            onAdd={addPerson}
            onRemove={removePerson}
            placeholder={t('managerLists.form.personPlaceholder')}
            emptyText={t('managerLists.form.noPeople')}
            removeLabel={t('managerLists.form.removePerson')}
          />
        </div>
      </div>
    </FormDialog>
  )
}
